import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTransactions } from '@/state/transactions'
import type { TransactionFilters, Transaction, TransactionStatus, TransactionType } from '@/models/transaction'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/ui/loading'
import { Eye, } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'

const statusColors: Record<TransactionStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
}

const typeColors: Record<TransactionType, string> = {
  purchase: 'bg-blue-100 text-blue-800',
  commission: 'bg-purple-100 text-purple-800',
  withdrawal: 'bg-orange-100 text-orange-800',
  credit: 'bg-green-100 text-green-800',
  debit: 'bg-red-100 text-red-800'
}

export default function TransactionsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<TransactionFilters>({
    limit: 50
  })
  const [showFilters] = useState(false)

  const {
    transactions,
    pagination,
    dataIsLoading,
    dataError,

  } = useTransactions(filters)

  const handleStatusChange = (status: string) => {
    setFilters((prev: TransactionFilters) => ({
      ...prev,
      status: status === 'all' ? undefined : status as TransactionStatus
    }))
  }

  const handleTypeChange = (type: string) => {
    setFilters((prev: TransactionFilters) => ({
      ...prev,
      type: type === 'all' ? undefined : type as TransactionType
    }))
  }

  const handleLimitChange = (limit: string) => {
    setFilters((prev: TransactionFilters) => ({
      ...prev,
      limit: parseInt(limit)
    }))
  }

  const formatAmount = (amount: string, currency: string) => {
    const num = parseFloat(amount)
    return `${num.toFixed(2)} ${currency}`
  }



  const viewTransaction = (transaction: Transaction) => {
    navigate({ to: '/dashboard/funds/transactions/$id', params: { id: String(transaction.id) } })
  }

  if (dataError) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {t('pages.fundTransactions.loadError')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="w-full md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('pages.fundTransactions.title')}</h1>
          <p className="text-muted-foreground">
            {t('pages.fundTransactions.subtitle')}
          </p>
        </div>
        
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>{t('pages.fundTransactions.filters')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('pages.fundTransactions.filterDescription')}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium">{t('pages.fundTransactions.status')}</label>
                <Select onValueChange={handleStatusChange} defaultValue="all">
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.fundTransactions.allStatuses')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('pages.fundTransactions.allStatuses')}</SelectItem>
                    <SelectItem value="pending">{t('pages.fundTransactions.pending')}</SelectItem>
                    <SelectItem value="completed">{t('pages.fundTransactions.completed')}</SelectItem>
                    <SelectItem value="failed">{t('pages.fundTransactions.failed')}</SelectItem>
                    <SelectItem value="cancelled">{t('pages.fundTransactions.cancelled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">{t('pages.fundTransactions.type')}</label>
                <Select onValueChange={handleTypeChange} defaultValue="all">
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.fundTransactions.allTypes')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('pages.fundTransactions.allTypes')}</SelectItem>
                    <SelectItem value="purchase">{t('pages.fundTransactions.purchase')}</SelectItem>
                    <SelectItem value="commission">{t('pages.fundTransactions.commission')}</SelectItem>
                    <SelectItem value="withdrawal">{t('pages.fundTransactions.withdrawal')}</SelectItem>
                    <SelectItem value="credit">{t('pages.fundTransactions.credit')}</SelectItem>
                    <SelectItem value="debit">{t('pages.fundTransactions.debit')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">{t('pages.fundTransactions.limit')}</label>
                <Select onValueChange={handleLimitChange} defaultValue="50">
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.fundTransactions.selectLimit')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('pages.fundTransactions.transactionHistory')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {pagination ? t('pages.fundTransactions.showingTransactions', { count: transactions.length, total: pagination.total }) : t('common.messages.loading')}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {dataIsLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">{t('pages.fundTransactions.noTransactions')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>

                  {/* <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>

                  <TableHead className="text-right">Actions</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow className=' overflow-hidden border-b-1 border-b-border hover:bg-background' key={transaction.id}>
                    <TableCell className='rounded-l-3xl hover:bg-background'>
                      <Badge 
                        variant="secondary" 
                        className={typeColors[transaction.type]}
                      >
                        {transaction.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium hover:bg-background">
                      {formatAmount(transaction.amount, '$')}
                    </TableCell>
                    <TableCell className='hover:bg-background' >
                      <Badge 
                        variant="secondary" 
                        className={statusColors[transaction.status]}
                      >
                        {transaction.status}
                      </Badge>
                    </TableCell>
                    
                    <TableCell className="text-right rounded-r-3xl hover:bg-background">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewTransaction(transaction)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
